// RoomHeader — Classic / Modern 共通のルームヘッダ帯 (2026-06-12 抽出).
//
// 由来: もともと Classic の `ClassicLayout.tsx` 内 `TopBar` / `TopBarButton`
// (classic-sidebar-compaction.md §2.2「Modern メニューバーの前身」) と、Modern の
// `ModernLayout.tsx` 内 inline `<nav>` が **別々に手作業で揃えて** いた。padding /
// font / ボタン / 高さ / ルーム名位置のドリフトが繰り返し発生した (= ユーザ指摘) ため、
// 両者を 1 本の presentational component に構造的に統合する。
//
// 方針 (ユーザ確定 2026-06-12):
//   - 帯の **見た目 (DOM 構造・class・項目)** を Classic / Modern で完全一致させる。
//     各項目の onClick 先だけ各 layout の handler に差し替わる (= データ配列 `items`)。
//   - ルーム名は **左寄せ** に統一 (Modern 流。旧 Classic の `text-center` を廃止)。
//   - 帯高は `py-1.5` + 中身で決まる **自然高** (= Classic の固定 `TOPBAR_PX` 依存を
//     やめる)。grid に置く側は先頭 row を `auto` にして本 component の自然高に従わせる。
//
// 項目はベタ書き button 直列ではなく `RoomHeaderItem` の **データ配列** で受け取る
// (= 旧 TopBar の「Modern メニューバーの前身」設計を継承)。将来 Modern でメニュー /
// ハンバーガーに昇格する場合も、この `items` をそのまま別 presentation に流せる。

// 項目スコープ (自分用 / セッション / owner 管理). 色・🔒 アイコンは CSS 直書きでは
// なくこの scope から `RoomHeaderButton` 内で導出する (= 旧 TopBarScope 踏襲):
//   - 'personal': 自分のクライアントだけに効く set-once 系 (= 表示設定).
//   - 'session': この入室セッションに効く操作 (= 全画面 / 退室).
//   - 'owner':   owner 権限が要る管理操作 (= ルーム設定). owner 判定 (adminToken 有無)
//                に応じて控えめ (🔒) ⇄ 琥珀アクセントを切替える.
//   - 'help':    使い方マニュアル入口 (2026-09-22 作者フィードバック「もっと目立つ
//                ようにできないかな」への改修). 他の scope (青系 personal/session /
//                琥珀 owner) と被らないティール系アクセントに固定発色する.
//   - 'invite':  友達を招待 (room-invite-2026-10-01.md、2026-10-01 作者 fb「目立つように」)。
//                黄 (= owner 認証済 / selected 色) と混同しないよう、既存トークン
//                `--color-success` の緑で塗る (= 「誘う・広げる」前向きアクションの色)。
export type RoomHeaderScope = 'personal' | 'owner' | 'session' | 'help' | 'invite';

export interface RoomHeaderAction {
  readonly kind: 'action';
  /** 安定 key + 将来 Modern メニューでの項目識別子. */
  readonly id: string;
  /** ボタン表示ラベル. */
  readonly label: string;
  readonly onClick: () => void;
  /** toggle 系ボタンの押下状態 (aria-pressed). 非 toggle は undefined. */
  readonly pressed?: boolean;
  /** title (tooltip) / aria 補助. */
  readonly title?: string;
  /** 帯の右端へ隔離する項目 (= ルーム設定 / 退室、誤タップ回避). */
  readonly align?: 'start' | 'end';
  /** modal を開くボタンに aria-haspopup="dialog" を付ける (= 表示 / ルーム設定). */
  readonly opensDialog?: boolean;
  /**
   * 項目スコープ (自分用 / セッション / owner 管理). 色・🔒 アイコンはこの scope から
   * `RoomHeaderButton` 内で導出する (= CSS 直書きしない). 将来 Modern はこの scope で
   * メニューをセクション分けできる.
   */
  readonly scope?: RoomHeaderScope;
  /**
   * 新機能訴求の「New!」バッジ (= accent 色の小さいピル) をラベル末尾に付ける.
   * 絵ログ共有ボタンで初回のみ true → モーダルを開くと呼び出し側が false に切替える
   * (timelapseShareSeen の seen フラグ、consult 2026-07-08). 既定 (undefined/false) は
   * 従来どおりバッジなし.
   */
  readonly badge?: boolean;
  /**
   * 未読の印 (小さなドット)。「新機能の紹介」ボタン用 (whats-new-2026-10-15.md)。
   * New! ピルほど目立たせない控えめな印。aria 向けに sr-only の「未読あり」を添える。
   */
  readonly unreadDot?: boolean;
  /**
   * owner 権限が要ることを示す 🔒 をラベルに前置する。scope:'owner' の 🔒 と違い
   * ボタンを琥珀昇格 **させない** (青系のまま). New! バッジ持ちの owner-gated action
   * (= 絵ログ共有 / 過去キャンバス、全員に見せて非オーナーはログイン誘導) 用: ボタンを
   * 琥珀にすると New! ピルが埋もれるので、控えめな 🔒 だけでロックを示唆する
   * (2026-07-08 確定). 既定 (undefined/false) は 🔒 なし. 非オーナー (= ロック) の
   * ときだけ true を渡す (owner 認証したら呼び出し側が false にして 🔒 が即消える).
   */
  readonly locked?: boolean;
  /**
   * 押下不能にする (= HTML `disabled`)。2026-07-23 追補 (Classic Undo ボタン):
   * cancel 可能な stroke が無いとき等、状態依存で押せなくする item 用。既定
   * (undefined/false) は従来どおり常に押せる (= 既存項目の挙動は不変)。
   */
  readonly disabled?: boolean;
  /**
   * 使い方マニュアル (user-manual-2026-09-19.md §10.2) の `data-manual` id。
   * このボタンが registry の 1 項目にそのまま対応するときだけ渡す (= モーダル内の
   * 複数項目をまとめて開くボタン (「描画」等) には渡さない、§10.2 の「開くボタンに
   * 印を付けない」原則)。
   */
  readonly manualId?: string;
  /**
   * ラベル前に置く装飾アイコン (aria-hidden、意味はラベル/title が担う)。
   * 使い方ボタン (Modern ヘッダ) 等、Modern 系レイアウト専用の `ModernIcons.tsx` SVG を
   * 渡す想定 (2026-09-22)。Classic はテキストのみのボタン美意識を保つため付けない
   * (= 既定 undefined = 従来どおり、アイコン無しで描画).
   */
  readonly icon?: React.ReactNode;
}

/**
 * 任意ノードを帯に差し込む項目 (= action ボタンに収まらない UI 用)。Modern の
 * ワークスペース preset ピッカー (`<select>`) 等。`align` で左寄せ / 右端隔離を選べる
 * (action と同じ意味論)。Classic は使わない (= 後方互換、action だけで完結する)。
 */
export interface RoomHeaderCustom {
  readonly kind: 'custom';
  /** 安定 key. */
  readonly id: string;
  /** 描画するノード (= 呼び出し側が style/handler を内包する)。 */
  readonly node: React.ReactNode;
  /** 帯の右端へ隔離するか (action と同じ)。 */
  readonly align?: 'start' | 'end';
}
export type RoomHeaderItem = RoomHeaderAction | RoomHeaderCustom;

/**
 * Classic / Modern 共通のルームヘッダ帯.
 *
 * レイアウト: `align: 'start'` の自分用項目 (全画面 / 表示) を左寄せ、`align: 'end'`
 * の管理項目 (ルーム設定 / 退室) を右端クラスタへ隔離 (誤タップ回避). ルーム名は中央の
 * 伸縮スペースに **左寄せ** truncate 表示 (= 旧 Classic の text-center を廃止)。
 *
 * `ownerAuthenticated`: owner (= adminToken 保持) かどうか. scope:'owner' のボタン
 * (ルーム設定) の見た目を、未認証=控えめ+🔒 / 認証済=琥珀アクセント に切替えるため
 * `RoomHeaderButton` へ伝える (= 色・🔒 を CSS 直書きせず scope + この flag から導出).
 *
 * `layoutToggle`: Classic ⇄ Modern 切替トグル slot. 右端クラスタ (ルーム設定 / 退室)
 * の手前に置く. スマホ (= Modern 固定) では呼び出し側が undefined を渡すので何も出ない.
 *
 * `notice`: 帯中央の注意書き slot (2026-06-13、Modern「超テスト版」告知用). ルーム名
 * (flex-1) と後続スペーサ (flex-1) に挟まれて **中央寄り** に出る琥珀バッジ。owner ボタン
 * の solid 琥珀 (--color-accent + 黒文字) と紛れないよう **半透明 amber アウトライン**
 * (AlphaWarningBanner の text-amber-400 警告系譜) にする。狭幅では min-w-0 + truncate で
 * graceful に縮む (title 属性で全文は読める)。Classic は undefined を渡す = 何も出ない。
 *
 * 帯の見た目 (padding / gap / font / ボタン / 高さ / ルーム名左寄せ) は **この 1 箇所**
 * に集約される (= Classic / Modern でドリフトしない単一の真実)。grid に置く側は先頭 row
 * を `auto` にして `py-1.5` の自然高に従わせる (= Classic の固定 TOPBAR_PX 依存を廃止)。
 */
export const RoomHeader = ({
  items,
  roomLabel,
  ownerAuthenticated,
  layoutToggle,
  notice,
  retentionChip,
}: {
  readonly items: ReadonlyArray<RoomHeaderItem>;
  readonly roomLabel: string;
  readonly ownerAuthenticated: boolean;
  readonly layoutToggle?: React.ReactNode;
  readonly notice?: string;
  /**
   * ルーム名の隣に出す owner-only の retention 案内チップ slot (P1、2026-07-14)。
   * 中身 (fetch / tier 出し分け / 非オーナー時 null) は `RoomRetentionChip` が持つ。
   * Classic / Modern 両方が同じ node を渡す (= 一度で効く)。省略時は何も出ない。
   */
  readonly retentionChip?: React.ReactNode;
}): React.ReactNode => {
  const startItems = items.filter((it) => it.align !== 'end');
  const endItems = items.filter((it) => it.align === 'end');
  return (
    <nav
      aria-label="ルームメニュー"
      // 帯の見た目はここに集約 (= Classic / Modern 完全一致の単一の真実)。
      // px-3 gap-2 / py-1.5 の自然高 / border-b / panel 背景。shrink-0 は flex 親
      // (Modern のルート flex-col) で潰れないため。grid 親 (Classic) では無害。
      className="flex shrink-0 items-center gap-2 overflow-hidden border-[var(--color-border)] border-b bg-[var(--color-panel)] px-3 py-1.5"
    >
      {startItems.map((it) =>
        it.kind === 'custom' ? (
          <div key={it.id} className="shrink-0">
            {it.node}
          </div>
        ) : (
          <RoomHeaderButton key={it.id} item={it} ownerAuthenticated={ownerAuthenticated} />
        ),
      )}
      {/* ルーム名: 左寄せ項目と右端隔離項目の間の伸縮スペースに **左寄せ** truncate 表示.
          表示専用なので select-none (iPad Apple Pencil の Text Highlighter 抑止). */}
      <span
        className="min-w-0 flex-1 select-none truncate text-left text-[12px] font-bold text-white"
        title={roomLabel}
      >
        {roomLabel}
      </span>
      {/* retention 案内チップ (retentionChip slot、2026-07-14 P1)。ルーム名の直後に
          置く (= ルーム名の近く)。owner-only・tier 出し分け・非オーナー時 null は
          RoomRetentionChip 側が担うので、ここは slot を素通しするだけ。省略時は何も
          出ない (= Classic/Modern 以外や未配線でも無害)。 */}
      {retentionChip !== undefined ? <div className="shrink-0">{retentionChip}</div> : null}
      {/* 注意書きバッジ (notice slot、2026-06-13 Modern「超テスト版」告知用)。両隣の
          flex-1 (ルーム名 / 下のスペーサ) が余白を等分するので帯中央寄りに座る。狭幅では
          flex-1 が先に 0 へ潰れ、その後バッジ自体が min-w-0 + truncate で縮む (= ヘッダの
          ボタン群と干渉しない graceful 縮退、全文は title tooltip)。色は owner ボタンの
          solid 琥珀と紛れない半透明 amber アウトライン。表示専用なので select-none。 */}
      {notice !== undefined ? (
        <>
          <span
            role="note"
            title={notice}
            className="min-w-0 select-none truncate rounded-full border border-amber-400/50 bg-amber-400/10 px-2 py-0.5 text-[10px] font-bold text-amber-300"
          >
            {/* ⚠️ は装飾 (= 意味はテキストが担う)。truncate でも先頭に残って警告と分かる。 */}
            <span aria-hidden="true" className="mr-1">
              ⚠️
            </span>
            {notice}
          </span>
          <span aria-hidden="true" className="flex-1" />
        </>
      ) : null}
      {/* Classic ⇄ Modern 切替トグル. 右端クラスタ (ルーム設定 / 退室) の手前に置く.
          スマホ (= Modern 固定) では呼び出し側が undefined を渡すので何も出ない. */}
      {layoutToggle !== undefined ? <div className="shrink-0">{layoutToggle}</div> : null}
      {endItems.map((it) =>
        it.kind === 'custom' ? (
          <div key={it.id} className="shrink-0">
            {it.node}
          </div>
        ) : (
          <RoomHeaderButton key={it.id} item={it} ownerAuthenticated={ownerAuthenticated} />
        ),
      )}
    </nav>
  );
};

/**
 * ヘッダの 1 ボタン. 色・🔒 アイコンは CSS 直書きではなく **scope から導出** する:
 *   - scope:'owner' (= ルーム設定): owner 判定 (`ownerAuthenticated`) に追従.
 *       - 未認証: 青系 (--color-bg-soft) + 🔒 を前置 (一般ユーザに主張しない、別端末
 *         ログインの入口を兼ねる). 🔒 は意味の唯一の手段にしないため aria-hidden、
 *         ラベル + title (tooltip) で意味が通る.
 *       - 認証済: 琥珀/金アクセント (--color-accent, 黒文字で可読性確保).
 *   - scope:'personal' / 'session' / 未指定: 従来どおり青系 (--color-bg-soft).
 *     退室 (scope:'session', align:'end') も色・挙動据え置き (= 青系で警告色にしない).
 *   - scope:'help' (= 使い方): personal (青) / owner (琥珀) と被らないティール系
 *     アクセントに固定発色する (2026-09-22、作者フィードバック「もっと目立つように」).
 */
const RoomHeaderButton = ({
  item,
  ownerAuthenticated,
}: {
  readonly item: RoomHeaderAction;
  readonly ownerAuthenticated: boolean;
}): React.ReactNode => {
  const isOwnerItem = item.scope === 'owner';
  const ownerActive = isOwnerItem && ownerAuthenticated;
  const ownerLocked = isOwnerItem && !ownerAuthenticated;
  const isHelpItem = item.scope === 'help';
  const isInviteItem = item.scope === 'invite';
  // scope から variant を導出 (= CSS 直書きしない). 認証済 owner は琥珀アクセント、
  // 使い方 (help) はティール系固定、それ以外 (未認証 owner 含む) は従来の青系.
  const variantClass = ownerActive
    ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black hover:brightness-95'
    : isHelpItem
      ? 'border-slate-600 bg-gradient-to-b from-slate-50 via-slate-200 to-slate-400 text-slate-900 shadow-[inset_0_1px_0_#fff,inset_0_-1px_0_rgba(0,0,0,0.15),0_3px_0_#1e293b,0_5px_8px_rgba(0,0,0,0.4)] hover:from-white hover:to-slate-300 active:translate-y-[2px] active:shadow-[inset_0_1px_0_#fff,0_1px_0_#1e293b]'
      : isInviteItem
        ? 'border-emerald-700 bg-[var(--color-success)] text-slate-900 shadow-[0_2px_0_#065f46] hover:brightness-105 active:translate-y-[1px] active:shadow-none'
        : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]';
  return (
    <button
      type="button"
      onClick={item.onClick}
      title={item.title}
      disabled={item.disabled}
      data-manual={item.manualId}
      // disabled 時は他 item の見た目に影響しない追加クラス (opacity + カーソル).
      // ブラウザ既定の disabled 挙動 (クリック不可) に加え、視覚的にも押せないと
      // 分かるようにする (= 2026-07-23 追補、Classic Undo ボタン).
      className={`shrink-0 whitespace-nowrap rounded border px-3 py-0.5 text-[11px] font-bold ${variantClass} ${item.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
      {...(item.pressed !== undefined ? { 'aria-pressed': item.pressed } : {})}
      {...(item.opensDialog ? ({ 'aria-haspopup': 'dialog' } as const) : {})}
    >
      {/* 🔒 は未認証 owner 項目 (ownerLocked) または owner-gated action (item.locked、
          絵ログ / 過去キャンバス) の前置アイコン. 後者は琥珀昇格せず青系のまま 🔒 だけで
          ロックを示唆する (New! を埋もれさせない、2026-07-08). 装飾なので aria-hidden
          (= 意味はラベル + title が担う、色/アイコンを意味の唯一の手段にしない a11y 配慮). */}
      {ownerLocked || item.locked ? (
        <span aria-hidden="true" className="mr-0.5">
          🔒
        </span>
      ) : null}
      {item.icon ? (
        <span aria-hidden="true" className="mr-1 inline-flex items-center align-middle">
          {item.icon}
        </span>
      ) : null}
      {item.label}
      {/* 新機能訴求バッジ (= accent 塗りの「New!」ピル). 初回のみ出て、押すと消える
          (呼び出し側が item.badge を false に切替える). aria-hidden にはせず「New!」を
          読み上げさせる (= 意味のある告知). 2026-07-08: 発見性優先で少し大きめ + pulse
          点滅 + ring で視線を引く (accent 塗り + 黒文字 = #f5c842 上で高コントラスト).
          inline-block なのでボタン高は据え置き, レイアウトは崩さない. */}
      {item.unreadDot ? (
        <span
          data-testid="unread-dot"
          className="ml-1 inline-block h-2 w-2 rounded-full bg-[var(--color-accent)] align-middle ring-1 ring-white/70"
        >
          <span className="sr-only">未読あり</span>
        </span>
      ) : null}
      {item.badge ? (
        <span className="ml-1 inline-block animate-pulse rounded-full bg-[var(--color-accent)] px-1.5 py-0.5 align-middle text-[9px] font-bold text-black leading-none shadow ring-1 ring-white/70">
          New!
        </span>
      ) : null}
    </button>
  );
};
